import {UploadFormModel} from "./upload-form-model";
import {
  isExisting,
  isLimitedPositiveInteger,
  isNonRequiredTextOfMaxLength,
  isNotInFuture,
  isRequiredTextOfMaxLength,
  isValidDateRange,
  isValidRange
} from "../validations";
import {MAX_CHARACTERS_IN_TEXT} from "../utils";
import {EcgSymptom, MAX_ECG_SYMPTOM_DESCRIPTION_LENGTH, MAX_ECG_SYMPTOMS} from "./upload-model";
import {canParseDateTime} from "../../tools/date-time-utils";
import {isValidHeightOrWeightValue} from "./is-valid-height-or-weight-value";

export const uploadFormMutuallyDependentProperties: (keyof UploadFormModel)[] = ["goToBedTime", "wakeUpTime"];

export interface UploadDataValidationConfig {
  ecgTechnicianAnalysisOnlyEnabled: boolean;
  initiateTime?: string;
  now?: string;
}

export function createMeasurementFormValidationMap(uploadData: UploadFormModel, config: UploadDataValidationConfig) {

  return new Map<keyof UploadFormModel, () => boolean>()
    .set("goToBedTime", () => isNotBloodPressureMeasurement(uploadData) || isValidDateRange(uploadData.goToBedTime, uploadData.wakeUpTime))
    .set("wakeUpTime", () => isNotBloodPressureMeasurement(uploadData) || isValidDateRange(uploadData.goToBedTime, uploadData.wakeUpTime))
    .set("gender", () => isExisting(uploadData.gender))
    .set("age", () => validDateOfBirthOrAge())
    .set("height", () => isValidHeightOrWeightValue(uploadData.height, uploadData.measurementType, config.ecgTechnicianAnalysisOnlyEnabled))
    .set("weight", () => isValidHeightOrWeightValue(uploadData.weight, uploadData.measurementType, config.ecgTechnicianAnalysisOnlyEnabled))
    .set("waistline", () =>
      isNotBloodPressureMeasurement(uploadData)
      || uploadData.waistline === undefined
      || uploadData.waistline === null
      || isValidRange(30, 300, uploadData.waistline)
    )
    .set("cholesterol", () =>
      isNotBloodPressureMeasurement(uploadData)
      || uploadData.cholesterol === undefined
      || uploadData.cholesterol === null
      || isValidRange(0.1, 20, uploadData.cholesterol)
    )
    .set("profession", () => isNonRequiredTextOfMaxLength(40, uploadData.profession))
    .set("symptoms", () => areSymptomsValid(uploadData))
    .set("reasonForStudy", () => isRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.reasonForStudy))
    .set("currentMedication", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.currentMedication))
    .set("sleepDiary", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.sleepDiary))
    .set("patientFeedback", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.patientFeedback))
    .set("measurementNurseComments", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.measurementNurseComments))
    .set("diary", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.diary))
    .set("diagnosedHeartDisease", () => isNonRequiredTextOfMaxLength(MAX_CHARACTERS_IN_TEXT, uploadData.diagnosedHeartDisease))
    .set("measurementStartTime", () => isMeasurementStartTimeValid(uploadData))
    .set("dateOfBirth", () => validDateOfBirthOrAge() && isDateOfBirthBeforeInitiateTime() && isDateOfBirthAfterOrAtMinimum());

  function validDateOfBirthOrAge() {
    return isExisting(uploadData.dateOfBirth) || isLimitedPositiveInteger(150, uploadData.age);
  }

  function isDateOfBirthAfterOrAtMinimum() {
    if (!uploadData.dateOfBirth) {
      return true;
    }
    return uploadData.dateOfBirth >= "1900-01-01";
  }

  function isDateOfBirthBeforeInitiateTime() {
    if (!uploadData.dateOfBirth) {
      return true;
    }
    if (config.initiateTime) {
      return uploadData.dateOfBirth < config.initiateTime.substring(0, 10);
    }
    if (config.now) {
      return uploadData.dateOfBirth < config.now.substring(0, 10);
    }
    return true;
  }

}

function isNotBloodPressureMeasurement(uploadData: UploadFormModel) {
  return uploadData.measurementType !== "BLOOD_PRESSURE"
    && uploadData.measurementType !== "HOME_BLOOD_PRESSURE";
}

function areSymptomsValid(uploadData: UploadFormModel): boolean {
  if (uploadData.measurementType !== "SYMPTOM_HOLTER"
    && uploadData.measurementType !== "HOME_SYMPTOM_HOLTER") {
    return true;
  }
  const symptoms = uploadData.symptoms || Array<EcgSymptom>();

  const validSymptomsCount = symptoms.filter((symptom) => !!symptom.time && !!symptom.description).length;
  const partiallyFilledSymptomsCount = symptoms.filter(
    (symptom) => [symptom.time, symptom.description].filter((p) => !!p).length === 1
  ).length;
  const countOfSymptomsWithTooLongDescription = symptoms.filter((symptom) => isSymptomDescriptionTooLong(symptom)).length;

  const allSymptomDatetimesValid = symptoms.every(symptom => symptom.time === undefined || canParseDateTime(symptom.time));

  return (
    allSymptomDatetimesValid &&
    validSymptomsCount >= 1 &&
    validSymptomsCount <= MAX_ECG_SYMPTOMS &&
    partiallyFilledSymptomsCount === 0 &&
    countOfSymptomsWithTooLongDescription === 0
  );
}

function isMeasurementStartTimeValid(uploadData: UploadFormModel): boolean {
  if (uploadData.measurementType === "SLEEP"
    || uploadData.measurementType === "HOME_SLEEP") {
    return true;
  }

  return isExisting(uploadData.measurementStartTime)
    && isNotInFuture(uploadData.measurementStartTime);
}

export function isSymptomDescriptionTooLong(symptom: EcgSymptom) {
  return symptom.description && symptom.description.length > MAX_ECG_SYMPTOM_DESCRIPTION_LENGTH;
}
